import React, { useState, useMemo, useEffect } from 'react'
import { Calculator } from 'lucide-react'

interface ESOPFinancingDrawerProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (data: { esopFinancing: any, companyName: string }) => Promise<void>
  isSubmitting?: boolean
  submitError?: string | null
  totalOptionsAvailable?: number
  grants?: any[]
  preSelectedGrant?: any | null 
}

const ESOPFinancingDrawer: React.FC<ESOPFinancingDrawerProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
  submitError = null,
  totalOptionsAvailable,
  grants = [],
  preSelectedGrant = null, 
}) => {
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null)
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false)
  const [isPlanDropdownOpen, setIsPlanDropdownOpen] = useState(false)
  const [isSalaryDropdownOpen, setIsSalaryDropdownOpen] = useState(false)
  const [financingPercentage, setFinancingPercentage] = useState<number>(0)
  const [quantity, setQuantity] = useState<string>('')
  const [salarySlabIndex, setSalarySlabIndex] = useState<number | null>(null)
  const [touched, setTouched] = useState({ quantity: false, salarySlabIndex: false })

  useEffect(() => {
    if (isOpen && preSelectedGrant) {
      const companyName = preSelectedGrant.companyName
      const planName = preSelectedGrant.planName || 'Default Plan'
      
      setSelectedCompany(companyName)
      setSelectedPlan(planName)
      setFinancingPercentage(0)
      setIsCompanyDropdownOpen(false)
      setIsPlanDropdownOpen(false)
      setIsSalaryDropdownOpen(false)
      setQuantity('')
      setSalarySlabIndex(null)
      setTouched({ quantity: false, salarySlabIndex: false })
    } else if (isOpen && !preSelectedGrant) {
      setSelectedCompany(null)
      setSelectedPlan(null)
      setFinancingPercentage(0)
      setIsCompanyDropdownOpen(false)
      setIsPlanDropdownOpen(false)
      setIsSalaryDropdownOpen(false)
      setQuantity('')
      setSalarySlabIndex(null)
      setTouched({ quantity: false, salarySlabIndex: false })
    }
  }, [isOpen, preSelectedGrant])

  const salarySlabs = [
    { label: 'Up to ₹2.5 Lakhs (0%)', taxRate: 0 },
    { label: '₹2.5 - ₹5 Lakhs (5%)', taxRate: 5 },
    { label: '₹5 - ₹10 Lakhs (20%)', taxRate: 20 },
    { label: '₹10 - ₹20 Lakhs (30%)', taxRate: 30 },
    { label: 'Above ₹20 Lakhs (30%)', taxRate: 30 },
  ]
const companies = useMemo(() => {
  if (grants.length === 0) return []
  const uniqueCompanies = Array.from(new Set(grants.map((g) => g.companyName).filter(Boolean)))
  return uniqueCompanies.sort()
}, [grants])

const plansForCompany = useMemo(() => {
  if (!selectedCompany) return []
  const companyGrants = grants.filter((g) => g.companyName === selectedCompany)
  const uniquePlans = Array.from(new Set(companyGrants.map((g) => g.planName || 'Default Plan')))
  return uniquePlans.sort()
}, [selectedCompany, grants])

  const selectedGrant = useMemo(() => {
    if (!selectedCompany || !selectedPlan) return null
    return grants.find(
      (g) => g.companyName === selectedCompany && 
            (g.planName || 'Default Plan') === selectedPlan 
            // &&
            // g.status === 'Active'
    )
  }, [selectedCompany, selectedPlan, grants])

  const maxOptions = useMemo(() => {
    return selectedGrant ? (selectedGrant.vestedOptions - selectedGrant.numberExercised) || 0 : totalOptionsAvailable || 0
  }, [selectedGrant, totalOptionsAvailable])

  const strikePrice = selectedGrant ? selectedGrant.exercisePriceOrSarValue || 0 : 0
  const fmv = selectedGrant ? selectedGrant.companyFMV || 0 : 0

  const errors = useMemo(() => {
    const errs: any = {}
    const qty = Number(quantity)
    if (touched.quantity) {
      if (!quantity) errs.quantity = 'Number of options is required'
      else if (qty < 1) errs.quantity = 'Must be at least 1 option'
      else if (qty > maxOptions) errs.quantity = `Cannot exceed ${maxOptions} options`
    }
    if (touched.salarySlabIndex && salarySlabIndex === null) {
      errs.salarySlabIndex = 'Salary slab is required'
    }
    return errs
  }, [quantity, salarySlabIndex, maxOptions, touched])

  const calculations = useMemo(() => {
    const qty = Number(quantity) || 0
    const taxRate = salarySlabIndex !== null ? salarySlabs[salarySlabIndex].taxRate : 0

    const exerciseCost = qty * strikePrice
    const paperGain = qty * (fmv - strikePrice)
    const perquisiteTax = paperGain > 0 ? (paperGain * taxRate) / 100 : 0
    const totalCost = exerciseCost + perquisiteTax

    const externallyFinanced = (totalCost * financingPercentage) / 100
    const selfFinanced = totalCost - externallyFinanced

    return {
      exerciseCost,
      paperGain,
      perquisiteTax,
      totalCost,
      externallyFinanced,
      selfFinanced,
    }
  }, [quantity, strikePrice, fmv, salarySlabIndex, financingPercentage])

  const formatCurrency = (value: number) => `₹${Math.round(value).toLocaleString('en-IN')}`

  const showResults = quantity && strikePrice && fmv && salarySlabIndex !== null && Object.keys(errors).length === 0

  const canSubmit = showResults && !isSubmitting

  const handleCompanyChange = (company: string) => {
    setSelectedCompany(company)
    setSelectedPlan(null)
    setQuantity('')
    setSalarySlabIndex(null)
    setTouched({ quantity: false, salarySlabIndex: false })
    setIsCompanyDropdownOpen(false)
  }

  const handlePlanChange = (plan: string) => {
    setSelectedPlan(plan)
    setIsPlanDropdownOpen(false)
  }

  const handleSubmit = async () => {
    if (!canSubmit) return
    const payload = {
      planName: selectedPlan === 'Default Plan' ? null : selectedPlan,
      quantity: Number(quantity),
      strikePrice: strikePrice,
      fmv: fmv,
      salarySlab: salarySlabs[salarySlabIndex!].label,
      salaryTaxRate: salarySlabs[salarySlabIndex!].taxRate,
      financingPercentage,
      calculations: {
        exerciseCost: calculations.exerciseCost,
        paperGain: calculations.paperGain,
        perquisiteTax: calculations.perquisiteTax,
        totalCost: calculations.totalCost,
        externallyFinanced: calculations.externallyFinanced,
        selfFinanced: calculations.selfFinanced,
      },
      requestedAt: new Date().toISOString(),
    }
    try {
      await onConfirm({ esopFinancing: payload, companyName: selectedCompany! })
    } catch (err) {
      console.error('Submission failed:', err)
    }
  }

  if (!isOpen) return null

  const isPreSelected = !!preSelectedGrant

  return (
    <>
      {/* Backdrop */}
      <div
        className='position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50'
        style={{ zIndex: 1040 }}
        onClick={isSubmitting ? undefined : onClose}
      />

      {/* Drawer */}
      <div
        className='position-fixed top-0 end-0 h-100 bg-white shadow-lg overflow-auto'
        style={{ width: '480px', maxWidth: '100vw', zIndex: 1050, paddingBottom: '100px' }}
      >
        <div className='p-4'>
          {/* Header */}
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <div className='d-flex align-items-center gap-3'>
              <Calculator className='text-primary' size={24} />
              <h5 className='fw-bold mb-0 fs-2 text-dark'>ESOP Financing</h5>
            </div>
            <button className='btn btn-sm btn-close' onClick={onClose} disabled={isSubmitting}></button>
          </div>

          {/* Error Alert */}
          {submitError && (
            <div className='alert alert-danger mb-4' role='alert'>
              <strong>Submission Failed:</strong> {submitError}
            </div>
          )}

          {/* Company Selection */}
          {grants.length > 0 && (
            <div className='mb-4 border-top pt-4'>
              <label className='form-label fw-semibold mb-1'>
                Select Company
              </label>
              <div className='position-relative'>
                <select
                  className='form-control form-control-sm form-select'
                  value={selectedCompany || ''}
                  onChange={(e) => handleCompanyChange(e.target.value)}
                  disabled={isSubmitting || isPreSelected}
                >
                  <option value=''>Select a company</option>
                  {companies.map((company, idx) => (
                    <option key={idx} value={company}>
                      {company}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Plan Selection */}
          {selectedCompany && (
            <div className='mb-4'>
              <label className='form-label fw-semibold mb-1'>
                Select Plan
              </label>
              <div className='position-relative'>
                <select
                  className='form-control form-control-sm form-select'
                  value={selectedPlan || ''}
                  onChange={(e) => handlePlanChange(e.target.value)}
                  disabled={isSubmitting || isPreSelected}
                >
                  <option value=''>Select a plan</option>
                  {plansForCompany.map((plan, idx) => (
                    <option key={idx} value={plan}>
                      {plan}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Exercise Parameters */}
          {selectedPlan && (
            <>
              <h6 className='fw-semibold mb-4'>Exercise Parameters</h6>

              {/* Quantity */}
              <div className='mb-4'>
                <label className='form-label fw-semibold mb-1 d-flex justify-content-between align-items-center'>
                  <span>
                    Number of Options to Exercise
                    <span className='text-primary ms-1'>*</span>
                  </span>
                  <span className='badge bg-light text-dark fw-normal'>
                    Available: {maxOptions.toLocaleString('en-IN')}
                  </span>
                </label>
                <input
                  type='number'
                  className={`form-control form-control-sm ${touched.quantity && errors.quantity ? 'is-invalid' : ''}`}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  onBlur={() => setTouched(prev => ({ ...prev, quantity: true }))}
                  onWheel={(e) => e.currentTarget.blur()}
                  min={0}
                  max={maxOptions}
                  placeholder='Enter number'
                  disabled={isSubmitting}
                />
                {touched.quantity && errors.quantity ? (
                  <div className='text-danger mt-1 small'>{errors.quantity}</div>
                ) : (
                  <div className='form-text text-muted'>
                    Maximum: {maxOptions.toLocaleString('en-IN')} options
                  </div>
                )}
              </div>

              {/* Strike Price */}
              <div className='mb-4'>
                <label className='form-label fw-semibold mb-1'>
                  Strike Price per Option (₹)
                </label>
                <input
                  type='number'
                  className='form-control form-control-sm bg-light'
                  value={strikePrice}
                  disabled
                  onWheel={(e) => e.currentTarget.blur()}
                />
                <div className='form-text text-muted small'>Price you pay per option</div>
              </div>

              {/* FMV */}
              <div className='mb-4'>
                <label className='form-label fw-semibold mb-1'>
                  FMV at Time of Exercise (₹)
                </label>
                <input
                  type='number'
                  className='form-control form-control-sm bg-light'
                  value={fmv}
                  disabled
                  onWheel={(e) => e.currentTarget.blur()}
                />
                <div className='form-text text-muted small'>Current fair market value per share</div>
              </div>

              {/* Salary Slab */}
              <div className='mb-4'>
                <label className='form-label fw-semibold mb-1'>
                  Salary Slab (for Perquisite Tax)
                  <span className='text-primary ms-1'>*</span>
                </label>
                <div className='position-relative'>
                  <select
                    className={`form-select form-control-sm ${touched.salarySlabIndex && errors.salarySlabIndex ? 'is-invalid' : ''}`}
                    value={salarySlabIndex ?? ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : Number(e.target.value)
                      setSalarySlabIndex(value)
                    }}
                    onBlur={() => setTouched(prev => ({ ...prev, salarySlabIndex: true }))}
                    disabled={isSubmitting}
                  >
                    <option value=''>Select your salary slab</option>
                    {salarySlabs.map((slab, idx) => (
                      <option key={idx} value={idx}>
                        {slab.label}
                      </option>
                    ))}
                  </select>

                  {touched.salarySlabIndex && errors.salarySlabIndex && (
                    <div className='text-danger mt-1 small'>
                      {errors.salarySlabIndex}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Cost Breakdown */}
          {showResults && (
            <>
              <h6 className='fw-semibold mb-3'>Estimated Cost Breakdown</h6>
              <div className='border rounded p-4 bg-light mb-4'>
                <div className='d-flex justify-content-between mb-3'>
                  <span className='text-muted'>Exercise Cost</span>
                  <span className='fw-semibold'>{formatCurrency(calculations.exerciseCost)}</span>
                </div>
                <div className='d-flex justify-content-between mb-3'>
                  <span className='text-muted'>Perquisite Value</span>
                  <span className='fw-semibold'>{formatCurrency(calculations.paperGain)}</span>
                </div>
                <div className='d-flex justify-content-between mb-3'>
                  <span className='text-muted'>Perquisite Tax</span>
                  <span className='fw-semibold'>{formatCurrency(calculations.perquisiteTax)}</span>
                </div>
                <div className='d-flex justify-content-between fw-bold pt-3 border-top'>
                  <span>Total Cost of Exercise</span>
                  <span className='text-primary'>{formatCurrency(calculations.totalCost)}</span>
                </div>
              </div>

              {/* Financing Options */}
              <div className='mb-4'>
                <h6 className='fw-semibold mb-3'>Financing Options</h6>

                <p className='text-muted mb-3 small'>
                  What percentage of the total cost would you like to get financing for?
                </p>

                {/* Slider */}
                <div className='mb-4'>
                  <div className='d-flex align-items-center gap-3'>
                    <div className='flex-grow-1 position-relative'>
                      <style>{`
                        .custom-range::-webkit-slider-thumb {
                          background: #ff5722;
                          border: 3px solid white;
                          box-shadow: 0 0 0 1px #ff5722;
                        }
                        .custom-range::-moz-range-thumb {
                          background: #ff5722;
                          border: 3px solid white;
                          box-shadow: 0 0 0 1px #ff5722;
                        }
                        .custom-range::-webkit-slider-runnable-track {
                          background: linear-gradient(to right, #ff5722 0%, #ff5722 ${financingPercentage}%, #dee2e6 ${financingPercentage}%, #dee2e6 100%);
                        }
                        .custom-range::-moz-range-track {
                          background: linear-gradient(to right, #ff5722 0%, #ff5722 ${financingPercentage}%, #dee2e6 ${financingPercentage}%, #dee2e6 100%);
                        }
                      `}</style>
                      <input
                        type='range'
                        className='form-range custom-range'
                        min='0'
                        max='100'
                        value={financingPercentage}
                        onChange={(e) => setFinancingPercentage(Number(e.target.value))}
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className='d-flex align-items-center gap-2 bg-white border rounded px-3 py-2'>
                      <input
                        type='number'
                        className='border-0 text-center fw-semibold'
                        style={{ width: '50px', outline: 'none' }}
                        value={financingPercentage}
                        onChange={(e) => {
                          const val = Number(e.target.value)
                          if (val >= 0 && val <= 100) setFinancingPercentage(val)
                        }}
                        min='0'
                        max='100'
                        disabled={isSubmitting}
                      />
                      <span className='text-muted'>%</span>
                    </div>
                  </div>
                </div>

                {financingPercentage > 0 ? (
                  <div className='bg-light border rounded p-4'>
                    <div className='d-flex justify-content-between mb-2'>
                      <span className='text-muted'>Self-Financed</span>
                      <span className='fw-semibold'>
                        {formatCurrency(calculations.selfFinanced)}
                      </span>
                    </div>
                    <div className='d-flex justify-content-between'>
                      <span className='text-muted'>Externally Financed</span>
                      <span className='fw-semibold text-primary'>
                        {formatCurrency(calculations.externallyFinanced)}
                      </span>
                    </div>
                  </div>
                ) : (
                  calculations.totalCost > 0 && (
                    <div className='bg-light border rounded p-4 text-center'>
                      <p className='mb-0 fw-medium text-muted'>
                        You will self-finance the entire exercise cost of{' '}
                        <strong className='text-dark'>
                          {formatCurrency(calculations.totalCost)}
                        </strong>
                      </p>
                    </div>
                  )
                )}
              </div>
            </>
          )}
        </div>

        {/* Fixed Bottom Button */}
        <div
          className='position-fixed bottom-0 end-0 bg-white border-top p-4'
          style={{ width: '480px', maxWidth: '100vw', zIndex: 1051 }}
        >
          <button
            className='btn btn-primary w-100 text-white fw-semibold'
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              backgroundColor: canSubmit ? '#D84E32' : '#cccccc',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {isSubmitting ? (
              <>
                <span className='spinner-border spinner-border-sm me-2' role='status' />
                Submitting...
              </>
            ) : (
              'Find Financing'
            )}
          </button>
        </div>
      </div>
    </>
  )
}

export default ESOPFinancingDrawer